import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { fileUrl } from "../lib/api";
import { usePromo } from "../context/PromoContext";
import { useCountdown, pad2 } from "../lib/useCountdown";
import { X, Copy, Check, ArrowRight } from "lucide-react";

const BLOCKED = ["/admin", "/cart", "/checkout", "/order-confirmed", "/login", "/register"];

export const PromoPopup = () => {
  const { campaign } = usePromo();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const c = campaign;
  const cd = useCountdown(c?.end_date);

  const key = c ? `hyppe_promo_${c.id}` : null;

  const shouldShow = useCallback(() => {
    if (!c) return false;
    const freq = c.popup_frequency || "once";
    if (freq === "every") return !sessionStorage.getItem(key);
    const last = localStorage.getItem(key);
    if (freq === "once") return !last;
    if (freq === "days") {
      if (!last) return true;
      const days = c.popup_repeat_days || 3;
      return (Date.now() - Number(last)) >= days * 86400000;
    }
    return !last;
  }, [c, key]);

  const markShown = useCallback(() => {
    if (!c) return;
    if ((c.popup_frequency || "once") === "every") sessionStorage.setItem(key, "1");
    else localStorage.setItem(key, String(Date.now()));
  }, [c, key]);

  const blocked = BLOCKED.some((p) => location.pathname.startsWith(p));
  const scopeOk = !c || c.page_scope !== "home" || location.pathname === "/";

  useEffect(() => {
    if (!c || !c.show_popup || blocked || !scopeOk || open) return;
    const t = setTimeout(() => {
      // Never stack on top of the first-visit onboarding popup.
      if (document.querySelector("[data-testid='onboarding-popup']")) return;
      if (shouldShow()) { markShown(); setOpen(true); }
    }, 1500);
    return () => clearTimeout(t);
  }, [c, blocked, scopeOk, open, shouldShow, markShown]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!open || !c) return null;

  const accent = c.accent_color || "#C5A059";
  const text = c.text_color || "#F4EFE6";
  const bg = c.bg_color || "#0A0A0A";

  const goCta = () => {
    setOpen(false);
    const link = (c.cta_link || "").trim();
    if (!link) { navigate("/shop"); return; }
    if (/^https?:\/\//i.test(link)) {
      if (c.cta_new_tab) window.open(link, "_blank", "noopener,noreferrer");
      else window.location.href = link;
    } else { navigate(link); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(c.coupon_code || ""); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* ignore */ }
  };

  return (
    <AnimatePresence>
      <motion.div data-testid="promo-popup" className="fixed inset-0 z-[125] flex items-center justify-center p-4 sm:p-6"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setOpen(false)} data-testid="promo-popup-backdrop" />
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
          transition={{ type: "spring", stiffness: 240, damping: 26 }}
          style={{ backgroundColor: bg, color: text }}
          className="relative w-full max-w-3xl border overflow-hidden grid md:grid-cols-2 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]">
          <div className="absolute inset-x-0 top-0 h-px promo-shimmer z-10" />
          <button data-testid="promo-popup-close" onClick={() => setOpen(false)} aria-label="Dismiss"
            className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center transition-colors" style={{ color: text }}>
            <X className="w-5 h-5" />
          </button>

          {/* Artwork */}
          <div className="relative h-48 md:h-auto md:min-h-[440px] overflow-hidden">
            <motion.img initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1 }}
              src={fileUrl(c.popup_image || c.desktop_image || c.mobile_image)} alt={c.title || "Campaign"}
              loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(0deg, ${bg}, transparent 60%)` }} />
          </div>

          {/* Content */}
          <div className="relative flex flex-col justify-center gap-4 p-7 sm:p-9">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.35em]" style={{ color: accent }}>{c.eyebrow || "Limited Time Only"}</p>
            <h2 data-testid="promo-popup-title" className="font-serif-display text-3xl sm:text-4xl leading-tight" style={{ color: text }}>{c.title}</h2>
            {c.subtitle && <p className="text-sm sm:text-base leading-relaxed" style={{ color: text, opacity: 0.72 }}>{c.subtitle}</p>}

            {c.coupon_code && (
              <div className="flex items-stretch mt-1 w-fit">
                <span className="font-mono-label text-[9px] uppercase tracking-[0.25em] flex items-center px-3 border border-r-0" style={{ borderColor: hexA(accent, 0.4), color: text, opacity: 0.7 }}>{c.coupon_label || "Use Code"}</span>
                <button data-testid="promo-popup-code" onClick={copyCode} title="Copy code"
                  className="group inline-flex items-center gap-2 px-4 py-2.5 border" style={{ borderColor: hexA(accent, 0.55), color: accent }}>
                  <span className="font-mono-label text-sm tracking-[0.2em] font-semibold">{c.coupon_code}</span>
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />}
                </button>
              </div>
            )}

            {!cd.expired && c.end_date && (
              <div data-testid="promo-popup-countdown" className="flex items-center gap-3 mt-1">
                <span className="font-mono-label text-[9px] uppercase tracking-[0.28em]" style={{ color: text, opacity: 0.5 }}>Ends in</span>
                <span className="font-serif-display text-lg" style={{ color: text }}>
                  {cd.days}d <span style={{ color: accent }}>·</span> {pad2(cd.hours)}h <span style={{ color: accent }}>·</span> {pad2(cd.minutes)}m
                </span>
              </div>
            )}

            <div className="pt-2">
              <button data-testid="promo-popup-cta" onClick={goCta}
                className="inline-flex items-center gap-2 px-8 py-3.5 font-mono-label text-xs uppercase tracking-[0.25em] transition-transform duration-300 hover:-translate-y-0.5"
                style={{ backgroundColor: accent, color: bg }}>
                {c.cta_text || "Shop Now"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

function hexA(hex, a) {
  const h = (hex || "#000000").replace("#", "");
  const n = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  const r = parseInt(n.slice(0, 2), 16) || 0;
  const g = parseInt(n.slice(2, 4), 16) || 0;
  const b = parseInt(n.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export default PromoPopup;
